/**
 * useItensContagemColumns - Column definitions for ItensContagemTab
 */
import React, { useMemo } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { type MRT_ColumnDef } from 'mantine-react-table';
import { ActionIcon, Badge } from '@mantine/core';
import { EditableNumberCell } from './EditableNumberCell';
import { EditableDateCell } from './EditableDateCell';
import { getValidadeStyle } from './ValidadeLegend';
import type { DadoContagem } from '../../hooks/useContagemData';

interface UseItensContagemColumnsParams {
  hideLotsAndValidity: boolean;
  isReadOnly: boolean;
  isEditMode: boolean;
  isMaster: boolean;
  modifiedItems: Map<number, Set<string>>;
  // N138: valor pode ser string — a coluna Validade manda DD/MM/YYYY.
  handleUpdateItemField: (rowIndex: number, field: string, value: number | string) => void;
  onDeleteItem?: (itemId: number, codigo: string, lote: string) => void;
  displayData: DadoContagem[];
}

export function useItensContagemColumns({
  hideLotsAndValidity,
  isReadOnly,
  isEditMode,
  isMaster,
  modifiedItems,
  handleUpdateItemField,
  onDeleteItem,
  displayData,
}: UseItensContagemColumnsParams) {
  const columns = useMemo<MRT_ColumnDef<DadoContagem>[]>(
    () => {
      const uniformHiddenSize = 105;
      const descricaoSize = hideLotsAndValidity ? 200 : 150;
      const clienteSize = hideLotsAndValidity ? uniformHiddenSize : 85;
      const regimeSize = hideLotsAndValidity ? uniformHiddenSize : 89;
      const produtoSize = hideLotsAndValidity ? 100 : 88;
      const validadeSize = 88;
      const sistemaContadoSize = hideLotsAndValidity ? uniformHiddenSize : 107;
      const faturarColetarSize = hideLotsAndValidity ? uniformHiddenSize : 97;
      const numericSize = hideLotsAndValidity ? uniformHiddenSize : 102;
      const smallNumericSize = hideLotsAndValidity ? uniformHiddenSize : 85;

      const baseColumns: MRT_ColumnDef<DadoContagem>[] = [
        {
          accessorKey: 'cliente',
          header: 'Cliente',
          size: clienteSize,
          Cell: ({ cell }) => (
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400" title={cell.getValue<string>() || '-'}>
              {cell.getValue<string>() || '-'}
            </span>
          ),
        },
        {
          accessorKey: 'regime',
          header: 'Regime',
          size: regimeSize,
          filterVariant: 'select',
          mantineFilterSelectProps: { data: ['Com Regime', 'Sem Regime'] },
          Cell: ({ cell }) => {
            const value = cell.getValue<string>();
            if (value === 'Com Regime') return <Badge color="green" variant="light" size="xs">Sim</Badge>;
            if (value === 'Sem Regime') return <Badge color="gray" variant="light" size="xs">Não</Badge>;
            return <span className="text-gray-400">-</span>;
          },
        },
        {
          accessorKey: 'produto',
          header: 'Produto',
          size: produtoSize,
          Cell: ({ cell }) => (
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">{cell.getValue<string>()}</span>
          ),
        },
        {
          accessorKey: 'descricao',
          header: 'Descrição',
          size: descricaoSize,
          Cell: ({ cell }) => (
            <span
              className={`text-xs truncate block ${hideLotsAndValidity ? 'max-w-[210px]' : 'max-w-[158px]'}`}
              title={cell.getValue<string>()}
            >
              {cell.getValue<string>()}
            </span>
          ),
        },
      ];

      if (!hideLotsAndValidity) {
        baseColumns.push(
          {
            accessorKey: 'lote',
            header: 'Lote',
            size: 77,
            Cell: ({ cell }) => <span className="font-mono text-xs">{cell.getValue<string>()}</span>,
          },
          {
            accessorKey: 'validade',
            header: 'Validade',
            size: validadeSize,
            // N138 (Alana): MASTER pode corrigir a validade aqui. Mesma regra do
            // Segregado (`isReadOnly || !isMaster`) — quem não é MASTER continua
            // vendo o texto com o alerta de vencimento, como antes.
            //
            // ⚠️ RISCO CONHECIDO, e é por isso que é MASTER-only: no backend,
            // `ItemContagemImportado.save()` chama `calcular_campos_derivados()`,
            // que roda `_calcular_quantidade_coletar()`. Ou seja, mudar a
            // validade RECALCULA A SEGREGAÇÃO do item (quantidade_coletar +
            // coletar) usando a regra do cliente. Isso é o comportamento
            // desejado — corrigir uma validade errada tem MESMO de corrigir a
            // segregação — mas não é um efeito óbvio para quem edita, então a
            // célula avisa no title e a tela avisa antes de salvar.
            Cell: ({ cell, row }) => {
              const diasParaVencer = row.original.diasParaVencer;
              const nivel1 = row.original.sinalizar_vencimento_1 ?? 90;
              const nivel2 = row.original.sinalizar_vencimento_2 ?? 120;
              const nivel3 = row.original.sinalizar_vencimento_3 ?? 180;
              const { textColor, showIcon } = getValidadeStyle(diasParaVencer, nivel1, nivel2, nivel3);
              const podeEditar = isMaster && !isReadOnly && !hideLotsAndValidity;

              if (!podeEditar) {
                return (
                  <span className={`flex items-center gap-1 ${textColor}`}>
                    {cell.getValue<string>() || '-'}
                    {showIcon && <AlertTriangle className="w-3 h-3" />}
                  </span>
                );
              }

              const rowIndex = displayData.findIndex(d =>
                d.produto === row.original.produto && d.lote === row.original.lote && d.cliente === row.original.cliente
              );
              return (
                <div className="flex items-center gap-1">
                  <EditableDateCell
                    value={cell.getValue<string>() || ''}
                    rowIndex={rowIndex}
                    fieldName="validade"
                    isReadOnly={false}
                    isModified={modifiedItems.get(rowIndex)?.has('validade') || false}
                    onUpdate={handleUpdateItemField}
                  />
                  {showIcon && <AlertTriangle className={`w-3 h-3 flex-shrink-0 ${textColor}`} />}
                </div>
              );
            },
          }
        );
      }

      baseColumns.push(
        {
          accessorKey: 'sistema',
          header: 'Sistema',
          size: sistemaContadoSize,
          enableColumnFilter: false,
          mantineTableBodyCellProps: { style: { textAlign: 'center', justifyContent: 'center' } },
          Cell: ({ row, cell }) => {
            const rowIndex = displayData.findIndex(d =>
              d.produto === row.original.produto && d.lote === row.original.lote && d.cliente === row.original.cliente
            );
            if (hideLotsAndValidity) return <span className="text-sm font-semibold text-center block">{cell.getValue<number>()}</span>;
            return (
              <EditableNumberCell
                value={cell.getValue<number>()}
                rowIndex={rowIndex}
                fieldName="sistema"
                isReadOnly={isReadOnly}
                isModified={modifiedItems.get(rowIndex)?.has('sistema') || false}
                onUpdate={handleUpdateItemField}
              />
            );
          },
        },
        {
          accessorKey: 'contado',
          header: 'Contado',
          size: sistemaContadoSize,
          enableColumnFilter: false,
          mantineTableBodyCellProps: { style: { textAlign: 'center', justifyContent: 'center' } },
          Cell: ({ row, cell }) => {
            const rowIndex = displayData.findIndex(d =>
              d.produto === row.original.produto && d.lote === row.original.lote && d.cliente === row.original.cliente
            );
            // N171: item nunca bipado mostra `—`, nao `0`. Um item que nao
            // existe no hospital estava se lendo como "conferido, deu zero" —
            // foi o que fez a contagem 1800 parecer contada.
            const nuncaContado = (row.original as { nuncaContado?: boolean }).nuncaContado === true;
            if (hideLotsAndValidity) {
              return (
                <span className="text-sm font-semibold text-center block">
                  {nuncaContado ? '—' : cell.getValue<number>()}
                </span>
              );
            }
            if (nuncaContado && isReadOnly) {
              return (
                <span
                  className="text-sm text-center block text-gray-400"
                  title="Item ainda não conferido pelo auditor"
                >
                  —
                </span>
              );
            }
            // ponytail/N172: em modo EDIÇÃO a célula continua mostrando `0` e
            // não `—`. Deixar em branco exigiria distinguir "vazio" de "zero"
            // dentro do EditableNumberCell — e hoje `handleCommit` ignora um
            // commit igual ao valor atual, então digitar `0` sobre um item
            // nunca contado (valor efetivo 0) viraria no-op e a Alana perderia
            // justamente a confirmação de falta. Quem sinaliza aqui é a coluna
            // Faltas (vermelha) e o `—` do modo leitura. Débito registrado.
            return (
              <EditableNumberCell
                value={cell.getValue<number>()}
                rowIndex={rowIndex}
                fieldName="contado"
                isReadOnly={isReadOnly}
                isModified={modifiedItems.get(rowIndex)?.has('contado') || false}
                onUpdate={handleUpdateItemField}
              />
            );
          },
        },
        {
          accessorKey: 'faturar',
          header: 'Faturar',
          size: faturarColetarSize,
          enableColumnFilter: false,
          mantineTableBodyCellProps: ({ cell }) => ({
            style: {
              backgroundColor: (cell.getValue<number>() || 0) > 0 ? 'rgba(147, 51, 234, 0.1)' : undefined,
              textAlign: 'center', justifyContent: 'center'
            }
          }),
          Cell: ({ cell }) => {
            const value = cell.getValue<number>() || 0;
            return (
              <span className={`text-center font-semibold block ${value > 0 ? 'text-purple-700 dark:text-purple-400' : 'text-gray-400'}`}>
                {value > 0 ? value : '-'}
              </span>
            );
          },
        },
        {
          accessorKey: 'coletar',
          header: 'Segregar',
          size: faturarColetarSize,
          enableColumnFilter: false,
          mantineTableBodyCellProps: ({ cell }) => ({
            style: {
              backgroundColor: (cell.getValue<number>() || 0) > 0 ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.05)',
              textAlign: 'center', justifyContent: 'center'
            }
          }),
          Cell: ({ cell }) => {
            const value = cell.getValue<number>() || 0;
            return (
              <span className={`text-center font-semibold block ${value > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-gray-400'}`}>
                {value > 0 ? value : '-'}
              </span>
            );
          },
        },
        {
          accessorKey: 'segregado',
          header: 'Segregado',
          size: sistemaContadoSize,
          enableColumnFilter: false,
          mantineTableBodyCellProps: ({ cell }) => ({
            style: {
              backgroundColor: (cell.getValue<number>() || 0) > 0
                ? 'rgba(139, 92, 246, 0.12)'
                : 'rgba(139, 92, 246, 0.04)',
              textAlign: 'center', justifyContent: 'center'
            }
          }),
          Cell: ({ row, cell }) => {
            const rowIndex = displayData.findIndex(d =>
              d.produto === row.original.produto && d.lote === row.original.lote && d.cliente === row.original.cliente
            );
            if (hideLotsAndValidity) return <span className="text-sm font-semibold text-center block">{cell.getValue<number>()}</span>;
            return (
              <EditableNumberCell
                value={cell.getValue<number>()}
                rowIndex={rowIndex}
                fieldName="segregado"
                isReadOnly={isReadOnly || !isMaster}
                isModified={modifiedItems.get(rowIndex)?.has('segregado') || false}
                onUpdate={handleUpdateItemField}
              />
            );
          },
        },
        {
          accessorKey: 'coletado',
          header: 'Coletado',
          size: Math.round(numericSize * 1.05),
          enableColumnFilter: false,
          mantineTableBodyCellProps: ({ cell }) => ({
            style: {
              backgroundColor: (cell.getValue<number>() || 0) > 0 ? 'rgba(59, 130, 246, 0.1)' : 'rgba(156, 163, 175, 0.1)',
              textAlign: 'center', justifyContent: 'center'
            }
          }),
          Cell: ({ cell }) => {
            const value = cell.getValue<number>() || 0;
            return (
              <span className={`text-center font-semibold block ${value > 0 ? 'text-blue-700 dark:text-blue-400' : 'text-gray-400'}`}>
                {value > 0 ? value : '-'}
              </span>
            );
          },
        },
        {
          accessorKey: 'sobras',
          header: 'Sobras',
          size: smallNumericSize,
          enableColumnFilter: false,
          mantineTableBodyCellProps: ({ cell }) => ({
            style: {
              backgroundColor: cell.getValue<number>() > 0 ? 'rgba(34, 197, 94, 0.1)' : undefined,
              textAlign: 'center', justifyContent: 'center'
            }
          }),
          Cell: ({ cell }) => {
            const value = cell.getValue<number>();
            return (
              <span className={`text-center block ${value > 0 ? 'text-green-600 font-bold' : ''}`}>
                {value > 0 ? `+${value}` : value}
              </span>
            );
          },
        },
        {
          accessorKey: 'faltas',
          header: 'Faltas',
          size: smallNumericSize,
          enableColumnFilter: false,
          mantineTableBodyCellProps: ({ cell }) => ({
            style: {
              backgroundColor: cell.getValue<number>() > 0 ? 'rgba(239, 68, 68, 0.1)' : undefined,
              textAlign: 'center', justifyContent: 'center'
            }
          }),
          Cell: ({ cell }) => {
            const value = cell.getValue<number>();
            return (
              <span className={`text-center block ${value > 0 ? 'text-red-600 font-bold' : ''}`}>
                {value > 0 ? `-${value}` : value}
              </span>
            );
          },
        }
      );

      return baseColumns;
    },
    [hideLotsAndValidity, isReadOnly, isMaster, modifiedItems, handleUpdateItemField, displayData]
  );

  const rowActions = isEditMode && onDeleteItem
    ? ({ row }: { row: { original: DadoContagem } }) =>
        row.original.id ? (
          <ActionIcon
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onDeleteItem(row.original.id!, row.original.produto, row.original.lote);
            }}
            variant="subtle"
            color="red"
            size="sm"
            title={`Excluir item ${row.original.produto} - ${row.original.lote}`}
          >
            <Trash2 size={14} />
          </ActionIcon>
        ) : null
    : undefined;

  return { columns, rowActions };
}
